import { clsx } from '@alexis/helpers/clsx';
import { translateText } from '@alexis/helpers/translation';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import FlightTimeFilter from '@components/flights/FlightTimeFilter';
import PillToggle from '@components/PillToggle';

import { getSelectedTimeBlocksForGenzoTracking } from '@helpers/flight';

import ArrowIcon from '@icons/arrow_short.svg';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type NewFlightTimesFilterProps = {
  isPadded?: boolean;
  genzoTrackApplyFilterActionEvent: any;
};

const NewFlightTimesFilter: React.FC<NewFlightTimesFilterProps> = ({
  isPadded,
  genzoTrackApplyFilterActionEvent,
}) => {
  const {
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    toggleMultiCitySingleFilter,
    activeLeg,
  } = useFlightSearchFilters();

  const { legFilterOptions } = useFlightSearchResults();

  const { isRoundTrip } = useFlightSearchPageParams();

  const [searchParams] = useSearchParams();

  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const selectedDepartLegDepartureTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (departLegDepartureTimeBlocksFilter.find((item) => item.leg === 0)
      ?.values as FlightFilterTimeBlock[]) || [];

  const selectedDepartLegArrivalTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (departLegArrivalTimeBlocksFilter.find((item) => item.leg === 0)
      ?.values as FlightFilterTimeBlock[]) || [];

  const selectedReturnLegDepartureTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (returnLegDepartureTimeBlocksFilter.find((item) => item.leg === 0)
      ?.values as FlightFilterTimeBlock[]) || [];

  const selectedReturnLegArrivalTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (returnLegArrivalTimeBlocksFilter.find((item) => item.leg === 0)
      ?.values as FlightFilterTimeBlock[]) || [];

  const [selectedLeg, setSelectedLeg] = useState<1 | 2>(1);

  const legFilter = legFilterOptions[activeLeg === 1 ? 1 : selectedLeg - 1];
  const isDepartLeg = activeLeg === 0 && selectedLeg === 1;

  // <!-- Departure -->
  const selectedDepartureTimes = isDepartLeg
    ? selectedDepartLegDepartureTimeBlocksForActiveLeg
    : selectedReturnLegDepartureTimeBlocksForActiveLeg;
  const departTimeString = isDepartLeg ? 'departLegDepartureTime' : 'returnLegDepartureTime';
  // <!-- ========= -->

  // <!-- Arrival -->
  const selectedArrivalTimes = isDepartLeg
    ? selectedDepartLegArrivalTimeBlocksForActiveLeg
    : selectedReturnLegArrivalTimeBlocksForActiveLeg;
  const arrivalTimeString = isDepartLeg ? 'departLegArrivalTime' : 'returnLegArrivalTime';
  // <!-- ========= -->

  const getQueryParamNamesForFlightTimeFilters = (
    selectedLegAndTime: FlightLegAndTime,
  ): FlightSearchResultSearchParams => {
    switch (selectedLegAndTime) {
      case 'departLegDepartureTime':
        return FlightSearchResultSearchParams.DepartLegDepartureTimeBlocks;
      case 'departLegArrivalTime':
        return FlightSearchResultSearchParams.DepartLegArrivalTimeBlocks;
      case 'returnLegDepartureTime':
        return FlightSearchResultSearchParams.ReturnLegDepartureTimeBlocks;
      case 'returnLegArrivalTime':
        return FlightSearchResultSearchParams.ReturnLegArrivalTimeBlocks;
    }
  };

  const getUpdatedSearchParam = (searchParams: string, timeBlock: FlightFilterTimeBlock) => {
    const timeBlocks = searchParams.split(',');
    if (timeBlocks.includes(timeBlock)) {
      return timeBlocks.filter((block) => block !== timeBlock && !!block).join(',');
    }

    return [...timeBlocks, timeBlock].filter(Boolean).join(',');
  };

  const handleFlightTimeBlockFilterToggled = (
    timeBlock: FlightFilterTimeBlock,
    selectedLegAndTime: FlightLegAndTime,
  ) => {
    recordChangeFilterAction();

    toggleMultiCitySingleFilter({
      activeLeg: 0,
      value: timeBlock,
      searchParamKey: getQueryParamNamesForFlightTimeFilters(selectedLegAndTime),
    });

    if (
      selectedLegAndTime === 'departLegDepartureTime' ||
      selectedLegAndTime === 'returnLegDepartureTime'
    ) {
      const departLegDepartTimeSearchParams =
        searchParams
          .get(FlightSearchResultSearchParams.DepartLegDepartureTimeBlocks)
          ?.split(':')[0] || null;
      const returnLegDepartTimeSearchParams =
        searchParams
          .get(FlightSearchResultSearchParams.ReturnLegDepartureTimeBlocks)
          ?.split(':')[0] || null;

      genzoTrackApplyFilterActionEvent('filter', 'applied', {
        departuretime_box_selected: getSelectedTimeBlocksForGenzoTracking(
          selectedLegAndTime === 'departLegDepartureTime'
            ? getUpdatedSearchParam(departLegDepartTimeSearchParams || '', timeBlock)
            : departLegDepartTimeSearchParams,
          selectedLegAndTime === 'returnLegDepartureTime'
            ? getUpdatedSearchParam(returnLegDepartTimeSearchParams || '', timeBlock)
            : returnLegDepartTimeSearchParams,
          isRoundTrip,
          activeLeg === 1,
        ),
      });
    } else {
      const departLegArrivalTimeSearchParams =
        searchParams
          .get(FlightSearchResultSearchParams.DepartLegArrivalTimeBlocks)
          ?.split(':')[0] || null;
      const returnLegArrivalTimeSearchParams =
        searchParams
          .get(FlightSearchResultSearchParams.ReturnLegArrivalTimeBlocks)
          ?.split(':')[0] || null;
      genzoTrackApplyFilterActionEvent('filter', 'applied', {
        arrivaltime_box_selected: getSelectedTimeBlocksForGenzoTracking(
          selectedLegAndTime === 'departLegArrivalTime'
            ? getUpdatedSearchParam(departLegArrivalTimeSearchParams || '', timeBlock)
            : departLegArrivalTimeSearchParams,
          selectedLegAndTime === 'returnLegArrivalTime'
            ? getUpdatedSearchParam(returnLegArrivalTimeSearchParams || '', timeBlock)
            : returnLegArrivalTimeSearchParams,
          isRoundTrip,
          activeLeg === 1,
        ),
      });
    }
  };

  return (
    <div className={clsx(styles.newFlightTimesFilter, isPadded && styles.padded)}>
      {isRoundTrip && activeLeg === 0 && (
        <PillToggle
          className={styles.pillToggle}
          items={[
            {
              label: translations.depart as string,
              selected: selectedLeg === 1,
              onClick: () => setSelectedLeg(1),
            },
            {
              label: translations.return as string,
              selected: selectedLeg === 2,
              onClick: () => setSelectedLeg(2),
            },
          ]}
        />
      )}

      {!!legFilter && (
        <div className={clsx(styles.categoryContent, styles.flightTime)}>
          <div className={styles.routeName}>
            {legFilter.departureCity!.name}

            <ArrowIcon className={styles.arrowIcon} />

            {legFilter.arrivalCity!.name}
          </div>

          <div className={styles.timeContainer}>
            <div className={clsx(styles.itemHeader, styles.flightTime)}>
              {translateText(
                translations.depart_from,
                locale,
                `${legFilter.departureCity!.name} (${legFilter.departureAirport?.code})`,
              )}
            </div>

            <FlightTimeFilter
              selectedTimeBlocks={selectedDepartureTimes}
              onTimeBlocksToggle={(timeBlock) =>
                handleFlightTimeBlockFilterToggled(timeBlock, departTimeString)
              }
            />
          </div>

          <div className={styles.timeContainer}>
            <div className={clsx(styles.itemHeader, styles.flightTime)}>
              {translateText(
                translations.arrive_in,
                locale,
                `${legFilter.arrivalCity!.name} (${legFilter.arrivalAirport?.code})`,
              )}
            </div>
            <FlightTimeFilter
              selectedTimeBlocks={selectedArrivalTimes}
              onTimeBlocksToggle={(timeBlock) =>
                handleFlightTimeBlockFilterToggled(timeBlock, arrivalTimeString)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewFlightTimesFilter;
